import DOMPurify from 'dompurify';

const NOTICE_SUPPRESSION_PREFIX = 'noticeDismissedUntil';
const ALLOWED_NOTICE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function toDate(value) {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
}

function sanitizeLinkUrl(url) {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (!ALLOWED_NOTICE_PROTOCOLS.has(parsedUrl.protocol)) {
      return '';
    }
    return parsedUrl.href;
  } catch (_error) {
    return '';
  }
}

function renderInlineMarkdown(value) {
  const escaped = escapeHtml(value);
  const withLinks = escaped.replace(/\[([^\]]+)\]\(([^)\s]+)\)/gu, (match, label, url) => {
    const safeUrl = sanitizeLinkUrl(url);
    if (!safeUrl) {
      return label;
    }
    return `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  return withLinks.replace(/\*\*([^*]+)\*\*/gu, '<strong>$1</strong>');
}

function flushParagraph(blocks, paragraphLines) {
  if (paragraphLines.length === 0) {
    return;
  }

  blocks.push(`<p>${paragraphLines.map(renderInlineMarkdown).join('<br>')}</p>`);
  paragraphLines.splice(0, paragraphLines.length);
}

function flushList(blocks, listItems) {
  if (listItems.length === 0) {
    return;
  }

  const items = listItems.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join('');
  blocks.push(`<ul>${items}</ul>`);
  listItems.splice(0, listItems.length);
}

export function getNoticeSuppressionKey(noticeId) {
  return `${NOTICE_SUPPRESSION_PREFIX}:${noticeId}`;
}

export function normalizeNotice(docSnapshot) {
  const data = docSnapshot.data();
  return {
    id: docSnapshot.id,
    ...data,
    startAtDate: toDate(data.startAt),
    endAtDate: toDate(data.endAt),
    createdAtDate: toDate(data.createdAt),
    updatedAtDate: toDate(data.updatedAt),
  };
}

export function isNoticeActive(notice, now = new Date()) {
  if (!notice?.isPublished || !notice.startAtDate || !notice.endAtDate) {
    return false;
  }

  return notice.startAtDate <= now && now <= notice.endAtDate;
}

export function selectCurrentNotice(notices, now = new Date()) {
  return [...notices]
    .filter((notice) => isNoticeActive(notice, now))
    .sort((left, right) => {
      const startDiff = right.startAtDate.getTime() - left.startAtDate.getTime();
      if (startDiff !== 0) {
        return startDiff;
      }

      return (right.createdAtDate?.getTime() || 0) - (left.createdAtDate?.getTime() || 0);
    })[0] || null;
}

export function renderNoticeMarkdown(markdown) {
  const lines = String(markdown || '').replace(/\r\n?/gu, '\n').split('\n');
  const blocks = [];
  const paragraphLines = [];
  const listItems = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    const listMatch = trimmed.match(/^[-*]\s+(.+)$/u);

    if (!trimmed) {
      flushParagraph(blocks, paragraphLines);
      flushList(blocks, listItems);
      return;
    }

    if (listMatch) {
      flushParagraph(blocks, paragraphLines);
      listItems.push(listMatch[1]);
      return;
    }

    flushList(blocks, listItems);
    paragraphLines.push(trimmed);
  });

  flushParagraph(blocks, paragraphLines);
  flushList(blocks, listItems);

  return DOMPurify.sanitize(blocks.join(''), {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'ul', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
}
